import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatCLP, dateLabel } from "../lib/format";
import { REGION_NAMES, comunasOf } from "../lib/chile";
import { ArrowsClockwise, CircleNotch, MapPin, Clock, Star, Trash, Plus, HandWaving } from "@phosphor-icons/react";

const EMPTY = { originRegion: "", originComuna: "", destinationRegion: "", destinationComuna: "", date: "", timeRange: "", startingPrice: "", notes: "" };

export default function ReturnTrips() {
  const { user } = useAuth();
  const isCarrier = user?.role === "CARRIER";
  const [trips, setTrips] = useState(null);
  const [mine, setMine] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/return-trips").then((r) => setTrips(r.data)).catch(() => setTrips([]));
    if (isCarrier) api.get("/return-trips/mine").then((r) => setMine(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const publish = async () => {
    if (!form.originComuna || !form.destinationComuna || !form.date) return toast.error("Completa origen, destino y fecha");
    setBusy(true);
    try {
      await api.post("/return-trips", form);
      toast.success("Retorno publicado");
      setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  const interest = async (id) => {
    try { await api.post(`/return-trips/${id}/interest`); toast.success("Le avisamos al transportista tu interés"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este retorno?")) return;
    try { await api.delete(`/return-trips/${id}`); toast.success("Retorno eliminado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (!trips) return <div className="flex justify-center py-24"><CircleNotch size={36} className="animate-spin text-[#FF5A36]" /></div>;

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6" data-testid="return-trips">
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A36]">Marketplace</span>
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1 mb-1 flex items-center gap-2"><ArrowsClockwise size={26} className="text-[#FF5A36]" /> Retornos disponibles</h1>
      <p className="text-sm text-zinc-500 mb-5">Transportistas que vuelven vacíos y ofrecen su viaje de retorno. Aprovecha tarifas convenientes.</p>

      {isCarrier && (
        <div className="rounded-2xl border border-zinc-200 bg-white p-5 mb-6" data-testid="return-publish">
          <h2 className="font-semibold mb-3 flex items-center gap-2"><Plus size={18} className="text-[#FF5A36]" /> Publicar mi retorno</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Sel label="Región origen" testid="ret-origin-region" value={form.originRegion} onChange={(v) => setForm({ ...form, originRegion: v, originComuna: "" })} options={REGION_NAMES} />
            <Sel label="Comuna origen" testid="ret-origin-comuna" value={form.originComuna} disabled={!form.originRegion} onChange={(v) => setForm({ ...form, originComuna: v })} options={comunasOf(form.originRegion)} />
            <Sel label="Región destino" testid="ret-dest-region" value={form.destinationRegion} onChange={(v) => setForm({ ...form, destinationRegion: v, destinationComuna: "" })} options={REGION_NAMES} />
            <Sel label="Comuna destino" testid="ret-dest-comuna" value={form.destinationComuna} disabled={!form.destinationRegion} onChange={(v) => setForm({ ...form, destinationComuna: v })} options={comunasOf(form.destinationRegion)} />
            <Inp label="Fecha" type="date" testid="ret-date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} />
            <Inp label="Horario" testid="ret-time" value={form.timeRange} onChange={(v) => setForm({ ...form, timeRange: v })} placeholder="09:00 - 12:00" />
            <Inp label="Precio base (opcional)" testid="ret-price" value={form.startingPrice} onChange={(v) => setForm({ ...form, startingPrice: v.replace(/\D/g, "") })} placeholder="120000" />
            <Inp label="Notas" testid="ret-notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} placeholder="Camión 3/4, 2 pallets" />
          </div>
          <button onClick={publish} disabled={busy} data-testid="ret-publish-btn"
            className="mt-4 px-6 py-2.5 rounded-full bg-[#FF5A36] text-white text-sm font-bold hover:bg-[#E04B2A] active:scale-95 disabled:opacity-60">
            {busy ? "Publicando…" : "Publicar retorno"}
          </button>
        </div>
      )}

      {trips.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-300 bg-white p-10 text-center text-zinc-500" data-testid="returns-empty">
          No hay retornos publicados por ahora.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="returns-list">
          {trips.map((t) => (
            <div key={t.id} className="rounded-2xl border border-zinc-200 bg-white p-4 flex flex-col" data-testid={`return-${t.id}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold flex items-center gap-1.5"><MapPin size={15} className="text-[#FF5A36]" /> {t.originComuna} → {t.destinationComuna}</span>
                {t.startingPrice ? <span className="font-display text-lg font-black">{formatCLP(Number(t.startingPrice))}</span> : null}
              </div>
              <div className="text-xs text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
                <span>{t.date ? dateLabel(t.date) : "Sin fecha"}</span>
                {t.timeRange && <span className="inline-flex items-center gap-1"><Clock size={12} /> {t.timeRange}</span>}
              </div>
              <div className="text-sm text-zinc-600 mt-2 flex items-center gap-1.5">
                {t.carrier?.name}
                {t.carrier?.rating ? <span className="text-xs text-zinc-500 flex items-center gap-0.5"><Star size={12} weight="fill" className="text-[#FF5A36]" />{t.carrier.rating}</span> : null}
              </div>
              {t.notes && <div className="text-xs text-zinc-500 mt-1">{t.notes}</div>}
              <div className="mt-auto pt-3 flex items-center justify-between">
                <span className="text-xs text-zinc-400">{t.interestedCount || 0} interesados</span>
                {isCarrier && mine.some((m) => m.id === t.id) ? (
                  <button onClick={() => remove(t.id)} data-testid={`return-delete-${t.id}`} className="text-sm font-semibold text-red-500 inline-flex items-center gap-1 hover:underline"><Trash size={14} /> Eliminar</button>
                ) : (
                  <button onClick={() => interest(t.id)} data-testid={`return-interest-${t.id}`} className="text-sm font-bold text-[#FF5A36] inline-flex items-center gap-1 hover:underline"><HandWaving size={15} /> Me interesa</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Sel({ label, value, onChange, options, disabled, testid }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">{label}</label>
      <select data-testid={testid} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full h-11 px-3 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36] disabled:bg-zinc-50">
        <option value="">Selecciona</option>
        {(options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function Inp({ label, value, onChange, placeholder, testid, type = "text" }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1.5 w-full h-11 px-3 rounded-xl border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A36]" />
    </div>
  );
}
